import { ThemeProvider } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useStore } from '@nanostores/react';

import { useSiteMetadataQuery } from '@klaviyo-mkt/gatsby-theme-core/src/hooks/useSiteMetadataQuery';
import { useAcademyCTAData } from '@klaviyo-mkt/gatsby-theme-core/src/hooks/useAcademyCTAData';
import useIsHydrated from '@klaviyo-mkt/gatsby-components/hooks/useIsHydrated';

import { useUniversalNavigationQuery } from '@klaviyo-mkt/gatsby-source-universal-navigation/src/hooks/useUniversalNavigationQuery';

import GlobalStyle from '@klaviyo-mkt/gatsby-components/shared/styles/global';
import { Header as UniversalHeader } from '@klaviyo-mkt/gatsby-components/organisms/header';
import KlaviyoStatusIndicator from '@klaviyo-mkt/gatsby-components/organisms/klaviyo-status-indicator';
import SkipButton from '@klaviyo-mkt/gatsby-components/atoms/skip-button';
import { UniversalFooter } from '@klaviyo-mkt/gatsby-components/organisms/universal-footer';
import { default as defaultTheme } from '@klaviyo-mkt/gatsby-components/shared/styles/themes/default';

import { convertLocaleForIconName } from '@klaviyo-mkt/gatsby-components/atoms/icon/utils';

import { $loggedIn } from '../stores/user';
import { sites } from '../constants';
import { PostLoginRedirect } from '../components/post-login-redirect';

import * as s from './styles';

import type { TIconName } from '@klaviyo-mkt/gatsby-components/atoms/icon/types';
import type { IUniversalMenuData } from '@klaviyo-mkt/gatsby-source-universal-navigation/src/types';
import type { IAcademyLayout } from './types';


const Layout = ({
  children,
  path = '',
  showFooter = true,
  showHeader = true,
  translations = [],
  onCredentials = false,
}: IAcademyLayout) => {
  const { t, i18n } = useTranslation();
  const loggedIn = useStore($loggedIn);
  const hasClientRendered = useIsHydrated();

  const siteMetadata = useSiteMetadataQuery();
  const ctas = useAcademyCTAData();
  /* @ts-ignore: this is valid, I can't narrow the return type */
  const universalNavData: { [x: string]: IUniversalMenuData; } = useUniversalNavigationQuery({ multiLocale: true });

  const { copyright, academy, footer, utility }: IUniversalMenuData = universalNavData?.[i18n.language] ?? universalNavData?.['en-US'] ?? {};

  return (
    <ThemeProvider theme={defaultTheme}>
      <s.SiteWrapper>
        <GlobalStyle />
        {showHeader && (
          <>
            <SkipButton />
            <UniversalHeader
              homeLink={`/${t('langCodes_localeLowerCase')}`}
              path={path}
              desktopLogoIconName={`KlaviyoPowerUpLogo${convertLocaleForIconName(i18n.language)}` as TIconName || 'Logo'}
              searchLink={`/${t('langCodes_localeLowerCase')}/search`}
              siteMetadata={siteMetadata}
              universalCtas={hasClientRendered ? ctas(onCredentials) : undefined}
              universalNavigationItems={academy}
              navigationStyle='power_up'
              utilityChildren={<KlaviyoStatusIndicator />}
              utilityNavigationItems={utility}
              academyLoggedIn={hasClientRendered ? (loggedIn ?? false) : undefined}
            />
          </>
        )}
        <PostLoginRedirect />
        <main id="site-content" role="main">
          {children}
        </main>
        {showFooter && (
          <UniversalFooter
            langCode={i18n.language.toLowerCase()}
            copyrightMenuItems={copyright}
            menuItems={footer}
            siteMetadata={siteMetadata}
            sites={sites}
            translations={translations}
          />
        )}
      </s.SiteWrapper>
    </ThemeProvider>
  );
};

export default Layout;
